import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import { Info } from "lucide-react";
import { useState } from "react";

function CashRecoveryCell({ value }) {
  const tone = value >= 50 ? "text-positive" : value >= 10 ? "text-warning" : "text-destructive";
  return <span className={`font-mono ${tone}`}>{formatPct(value, { signed: true })}</span>;
}

export default function Procurement() {
  const { data: matrix } = useSWR("/procurement/buy-sell-matrix", fetcher);
  const { data: reorder } = useSWR("/procurement/smart-reorder", fetcher);
  const [showInfo, setShowInfo] = useState(false);

  const rows = matrix?.matrix || [];
  const reorderRows = reorder?.items || [];

  // KPIs
  const totalPurc = rows.reduce((s, r) => s + (r.landing_cost || 0), 0);
  const totalSales = rows.reduce((s, r) => s + (r.sales_value || 0), 0);
  const cashRec = totalPurc ? ((totalSales - totalPurc) / totalSales) * 100 : 0;
  const reorderHigh = reorderRows.filter((r) => r.priority === "HIGH").length;

  return (
    <div>
      <PageHeader
        eyebrow="Procurement Intelligence"
        title="Buy versus sell, in one ledger."
        lede="Every product × vendor combination, scored by Cash Recovery. The harder the negative, the colder the cash."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <div className="bg-background p-5">
          <div className="label-caps">Total purchases</div>
          <div className="font-mono text-2xl mt-2">{formatIndianCurrency(totalPurc)}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">Cash recovery</div>
          <div className={`font-mono text-2xl mt-2 ${cashRec >= 0 ? "text-positive" : "text-destructive"}`}>{formatPct(cashRec, { signed: true })}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">Reorder pending</div>
          <div className="font-mono text-2xl mt-2">{reorderRows.length}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">High priority</div>
          <div className="font-mono text-2xl mt-2 text-destructive">{reorderHigh}</div>
        </div>
      </div>

      <Section
        eyebrow="Cash recovery matrix"
        title="Buy vs Sell"
        right={
          <button onClick={() => setShowInfo(!showInfo)} className="hairline px-3 h-8 flex items-center gap-2 text-xs">
            <Info className="w-3.5 h-3.5" /> What is Cash Recovery?
          </button>
        }
      >
        {showInfo && (
          <div className="hairline p-4 mb-4 bg-secondary/40 text-sm" data-testid="cash-recovery-info">
            <strong className="font-display">Cash Recovery</strong> = (sales − landing cost) ÷ sales × 100. It is the percentage of the season's buy you've already recovered through sales-to-date. It can go negative when most stock is unsold — that means you've spent more buying than you've earned selling.
          </div>
        )}
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Product</th>
                <th>Vendor</th>
                <th className="text-right">Sale price</th>
                <th className="text-right">Landing cost</th>
                <th className="text-right">Sell-thru</th>
                <th className="text-right">Cash Recovery</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid="buy-sell-row">
                  <td className="font-display text-sm truncate max-w-[260px]" title={r.product}>{r.product}</td>
                  <td className="text-muted-foreground truncate max-w-[160px]" title={r.vendor}>{r.vendor}</td>
                  <td className="text-right">{formatIndianCurrency(r.sales_value)}</td>
                  <td className="text-right">{formatIndianCurrency(r.landing_cost)}</td>
                  <td className="text-right">{formatPct(r.sell_through)}</td>
                  <td className="text-right"><CashRecoveryCell value={r.true_margin} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="hairline p-3">
              <div className="font-display text-sm truncate" title={r.product}>{r.product}</div>
              <div className="text-[11px] text-muted-foreground truncate">{r.vendor}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 font-mono text-xs">
                <div><div className="label-caps">Sell-thru</div>{formatPct(r.sell_through)}</div>
                <div><div className="label-caps">Recovery</div><CashRecoveryCell value={r.true_margin} /></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Smart reorder" title="What to repurchase, now">
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Product</th>
                <th>Vendor</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Cover (days)</th>
                <th className="text-right">Suggested PO</th>
              </tr>
            </thead>
            <tbody>
              {reorderRows.slice(0, 30).map((r, i) => (
                <tr key={i} data-testid="reorder-row">
                  <td>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest hairline ${
                      r.priority === "HIGH" ? "border-destructive text-destructive" :
                      r.priority === "MEDIUM" ? "border-warning text-warning" : ""
                    }`}>{r.priority}</span>
                  </td>
                  <td className="font-display text-sm truncate max-w-[240px]" title={r.product}>{r.product}</td>
                  <td className="text-muted-foreground truncate max-w-[160px]" title={r.vendor}>{r.vendor}</td>
                  <td className="text-right">{formatIndianGrouping(r.closing_qty)}</td>
                  <td className={`text-right ${r.cover_days < 30 ? "text-destructive" : ""}`}>{r.cover_days}</td>
                  <td className="text-right text-primary">{formatIndianGrouping(r.suggested_po_qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {reorderRows.slice(0, 20).map((r, i) => (
            <div key={i} className="hairline p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="font-display text-sm truncate min-w-0 flex-1" title={r.product}>{r.product}</div>
                <span className={`px-2 py-0.5 text-[10px] hairline shrink-0 ${
                  r.priority === "HIGH" ? "border-destructive text-destructive" : ""
                }`}>{r.priority}</span>
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{r.vendor}</div>
              <div className="grid grid-cols-3 gap-2 mt-2 font-mono text-xs">
                <div><div className="label-caps">Stock</div>{formatIndianGrouping(r.closing_qty)}</div>
                <div><div className="label-caps">Cover</div>{r.cover_days}d</div>
                <div><div className="label-caps">PO</div>{formatIndianGrouping(r.suggested_po_qty)}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
